// =============================================================================
// Central JWT auth module.
//
// One place to read/write the session, build authorized requests, decide
// whether the stored token is still usable, and react to a 401 by logging
// the user out and bouncing to /login. Components should call apiFetch (or
// at minimum authHeaders + handleAuthFailure) rather than recomputing this
// logic inline — the global fetch interceptor installed by installAuthFetchInterceptor
// covers the rest.
// =============================================================================

import * as refDataCache from './refDataCache';
import * as searchCache from './searchCache';
import * as dashCache from './dashCache';
import sessionStorage from './sessionStore';
import { clearSessionCaches } from './sessionCaches';

const SESSION_KEYS = [
  'authToken',
  'tokenExpiry',
  'userEmail',
  'userName',
  'userRole',
  'territoryId',
  'territoryName',
  'salesRepCode',
  'salesRepName',
  'userActions',
];

// Treat the token as expired one minute before the server's stated expiry so
// we don't race the JwtBearer "lifetime" check on the wire.
const EXPIRY_SKEW_MS = 60 * 1000;

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------
export function getToken() {
  return sessionStorage.getItem('authToken');
}

export function getUser() {
  return {
    email: sessionStorage.getItem('userEmail'),
    name: sessionStorage.getItem('userName'),
    role: sessionStorage.getItem('userRole'),
    territoryId: sessionStorage.getItem('territoryId'),
    territoryName: sessionStorage.getItem('territoryName'),
    salesRepCode: sessionStorage.getItem('salesRepCode'),
    salesRepName: sessionStorage.getItem('salesRepName'),
    actions: sessionStorage.getItem('userActions'),
  };
}

// -----------------------------------------------------------------------------
// Action entitlements (ActionMaster / RoleActions)
// -----------------------------------------------------------------------------
// The login response carries the role's entitled action ids as a comma-
// separated string (e.g. "00001,00004"), mirroring the JWT "actions" claim.
// hasAction('00002') gates UI affordances (buttons, edit icons). `fallback`
// is returned when no entitlements are stored at all — a session created
// before this feature shipped — so existing logins don't lose buttons until
// they re-login; a stored-but-empty string means "no actions" (default deny).
export function hasAction(actionId, fallback = false) {
  const stored = sessionStorage.getItem('userActions');
  if (stored == null) return fallback;
  return stored.split(',').includes(actionId);
}

// Role equivalence for scope checks — the single list every page should use
// instead of inlining `role === "admin" || role === "distr"`. "IT"
// (legacy "Shukla") is the IT super-admin Epicor RoleCode and scopes like
// Admin everywhere; Testers / USAHedRep are the remaining legacy
// admin-equivalent codes. Mirrors the API's Services/AdminRoles.cs.
export function isAdminOrDistrRole(role) {
  const r = (role || '').trim().toLowerCase();
  return r === 'admin' || r === 'distr'
      || r === 'it' || r === 'shukla'
      || r === 'testers' || r === 'usahedrep';
}

// Is this role a sales rep? Mirrors the API's SalesRoleVocabulary SalesRep
// spellings (SalesRep / ShuklaSalesRep / Rep). A distributor whose app login
// carries role "Rep" counts as a rep here, as it does on the server. With no
// argument it reads the signed-in user's role. UI-DSH-052: the dashboard hides
// every commission figure for a rep.
export function isSalesRepRole(role = sessionStorage.getItem('userRole')) {
  const r = (role || '').trim().toLowerCase();
  return r === 'salesrep' || r === 'shuklasalesrep' || r === 'rep';
}

// May this role run manual invoice generation (Reconcile CTO) and the N30
// maintenance jobs? Mirrors the API's CallerScope.MayRunInvoiceGeneration: a
// HEAD-OFFICE admin (admin role on D99 or no territory — isShuklaIt below) or
// CustomerService (SAL-GAP-06). A territory Admin (e.g. D58) no longer qualifies
// (SAL-GAP-67, 2026-10-19). Distr / reps never see them.
export function mayGenerateInvoicesRole(role) {
  const r = (role || '').trim().toLowerCase();
  return r === 'customerservice' || isShuklaIt();
}

// Role Access is IT only: an admin-equivalent role on the D99 root territory
// (or no territory claim). Mirrors RoleAccessController.IsShuklaIt, which
// re-checks server-side.
export function isShuklaIt() {
  const role = (sessionStorage.getItem('userRole') || '').trim().toLowerCase();
  const terr = (sessionStorage.getItem('territoryId') || '').toUpperCase();
  return ['shukla', 'it', 'admin', 'testers', 'usahedrep'].includes(role)
    && (terr === 'D99' || terr === '');
}

// Module-level access — DATA-DRIVEN: each auth-gated menu row carries
// Menu.RequiredActionId (SQL/032), the module's search/view action, and the
// /api/Common/Menu payload delivers it to both this app and the mobile app.
// A role without the action doesn't see the menu and can't open the page by
// URL (RequireAction / in-page guards). NULL/absent = unrestricted menu.
//
// The static map below is only a FALLBACK for menu payloads cached before
// the column shipped — the DB value always wins when present.
export const MENU_ACTION = {
  2064: '00001', // Sales Territory  — search/filter territories
  2065: '00010', // Sales Reps       — search/filter sales reps
  2071: '00014', // Customers        — search/filter customers
  2101: '00031', // Quotes           — search/filter quotes
  2104: '00048', // Orders           — search/filter orders
  2105: '00051', // Shipping         — search/filter shipments
  2079: '00054', // Dashboard        — view dashboard
};

// Accepts a full menu object (preferred) or a bare menuId (legacy callers).
// Fallback true inside hasAction = pre-entitlement sessions keep their menus
// until re-login.
export function canAccessMenu(menuOrId) {
  if (menuOrId && typeof menuOrId === 'object') {
    const required = menuOrId.requiredActionId ?? MENU_ACTION[Number(menuOrId.menuId)];
    return required ? hasAction(required, true) : true;
  }
  const required = MENU_ACTION[Number(menuOrId)];
  return required ? hasAction(required, true) : true;
}

export function getTokenExpiry() {
  const raw = sessionStorage.getItem('tokenExpiry');
  if (!raw) return null;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

export function isTokenExpired() {
  const expiryMs = getTokenExpiry();
  if (expiryMs == null) return false; // unknown → trust the server
  return Date.now() >= (expiryMs - EXPIRY_SKEW_MS);
}

export function isAuthenticated() {
  return !!getToken() && !isTokenExpired();
}

// -----------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------
export function persistAuth(data) {
  // GAP-28 (2026-10-31): signing in as a SECOND user without logging out kept
  // the first user's rep code, territory name and preloaded lists, because the
  // writes below only set the keys the new answer carries (a head-office login
  // has no salesRepCode, so the previous rep's code stayed). A new token is a
  // new session: wipe every per-user key, every sm_ cookie and the in-memory
  // caches first, then store the new user's values.
  if (data && data.token) {
    clearAuth();
    sessionStorage.clear();   // every sm_ cookie, not only SESSION_KEYS
    _redirectingToLogin = false;
  }
  if (data.token) sessionStorage.setItem('authToken', data.token);
  if (data.email) sessionStorage.setItem('userEmail', data.email);
  if (data.firstName || data.lastName) {
    sessionStorage.setItem(
      'userName',
      `${data.firstName || ''} ${data.lastName || ''}`.trim()
    );
  }
  if (data.role) sessionStorage.setItem('userRole', data.role);
  if (data.territoryId) sessionStorage.setItem('territoryId', data.territoryId);
  if (data.territoryName) sessionStorage.setItem('territoryName', data.territoryName);
  if (data.salesRepCode) sessionStorage.setItem('salesRepCode', data.salesRepCode);
  if (data.salesRepName) sessionStorage.setItem('salesRepName', data.salesRepName);
  if (data.tokenExpiry) sessionStorage.setItem('tokenExpiry', data.tokenExpiry);
  // Always store actions when the response includes the auth payload — an
  // empty string is meaningful (role with no entitlements = default deny).
  if (data.actions !== undefined) {
    sessionStorage.setItem('userActions', data.actions ?? '');
  }
}

export function clearAuth() {
  SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k));
  // Drop the preloaded reference-data lists, prefetched search results, and
  // cached dashboard config/data so the next sign-in starts cold (a different
  // user may have a different territory/rep scope).
  refDataCache.clear();
  searchCache.clear();
  dashCache.clear();
  clearSessionCaches();   // per-order caches (Order.js, Quote.js, OrderChainPanel.js)
}

// Boot-time guard. A session cookie can survive a browser restart when the
// browser is set to "continue where you left off", so on load we may hold a
// token whose server-side lifetime has already elapsed. Token-presence checks
// (e.g. the navbar's isLoggedIn) would then render stale logged-in chrome until
// the first API call bounces the user. Call this once at app boot to wipe an
// expired token up front so the UI starts in the correct signed-out state.
// Returns true if it cleared a stale session.
export function clearSessionIfExpired() {
  if (getToken() && isTokenExpired()) {
    clearAuth();
    return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// Logout — clears auth and sends the user to /login, preserving the path they
// were on so a successful sign-in can bounce them back. Idempotent so a burst
// of 401s from parallel requests doesn't fight over navigation.
// -----------------------------------------------------------------------------
let _redirectingToLogin = false;

export function logout({ returnTo } = {}) {
  if (_redirectingToLogin) return;
  _redirectingToLogin = true;
  clearAuth();
  const target = returnTo
    ?? (window.location.pathname + window.location.search + window.location.hash);
  const onLogin = window.location.pathname === '/login';
  if (onLogin) {
    _redirectingToLogin = false;
    return;
  }
  const url = '/login?returnTo=' + encodeURIComponent(target);
  // Hard nav so any in-flight component state is dropped — survives even
  // when called from outside the React tree (e.g. the fetch interceptor).
  window.location.assign(url);
}

// -----------------------------------------------------------------------------
// Request helpers
// -----------------------------------------------------------------------------
export function authHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// Wraps fetch. Auto-attaches the Authorization header to same-origin /api/*
// requests, refuses to call the API at all when the local token is already
// known-expired (saves a round-trip + bounces straight to login), and turns
// any 401 response into a logout.
export async function apiFetch(input, init = {}) {
  if (isTokenExpired()) {
    logout();
    throw new Error('Session expired');
  }
  const merged = {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...authHeaders(),
    },
  };
  const res = await fetch(input, merged);
  // Don't logout on 401s from anonymous auth endpoints (bad password / OTP)
  // — those carry the real user-facing error in the JSON body. Only kick
  // the session for 401s from protected endpoints.
  if (res.status === 401 && !isAuthEndpoint(input)) {
    logout();
  }
  return res;
}

// -----------------------------------------------------------------------------
// Global fetch interceptor — catches 401 from any code path that hasn't been
// migrated to apiFetch yet (the codebase has Bearer headers inlined in ~15
// places). Installed once from index.js at app boot. Safe to call twice;
// monkey-patch is idempotent via a marker on window.fetch.
// -----------------------------------------------------------------------------
const INSTALLED = '__authInterceptorInstalled';
// Bump this whenever the wrapped fetch logic changes. The marker on
// window.fetch carries the version that's *currently* installed, so a
// hot-reload (or any re-run of installAuthFetchInterceptor) can detect
// "old version still wrapping fetch" and replace it. Without this, the
// boot-time wrap survives every HMR cycle until a hard reload.
const INTERCEPTOR_VERSION = 3;

export function installAuthFetchInterceptor() {
  if (typeof window === 'undefined' || window.fetch == null) return;
  // Already wrapped with the current version → nothing to do.
  if (window.fetch[INSTALLED] === INTERCEPTOR_VERSION) return;
  // An older version is wrapping fetch — unwrap before re-wrapping so we
  // don't stack multiple interceptors on top of each other.
  if (window.fetch[INSTALLED] && window.fetch.__originalFetch) {
    window.fetch = window.fetch.__originalFetch;
  }
  const originalFetch = window.fetch.bind(window);
  const wrapped = async (...args) => {
    // Auto-inject Authorization on same-origin /api/* calls that didn't
    // already set one. Many legacy call sites use plain fetch() without
    // headers and pre-dated the [Authorize] sweep on CommonController etc.
    // — without this, every navigation after login hits 401 and bounces.
    const patched = withAuthHeader(args);
    const res = await originalFetch(...patched);
    // Only react to 401 from our own API — never log out for cross-origin
    // 401s (e.g. Avalara, address autocomplete) that the app might tolerate.
    // Also skip /api/Auth/* — those endpoints use 401 to signal bad creds /
    // expired OTP and carry the user-facing message in the body. Treating
    // those as "session kicked" wipes sessionStorage mid-login and makes
    // each retry race with the redirect lock — symptom: different error on
    // every attempt, login appearing to "not work".
    if (res.status === 401 && isOwnApiCall(patched[0]) && !isAuthEndpoint(patched[0])) {
      logout();
    }
    return res;
  };
  wrapped[INSTALLED] = INTERCEPTOR_VERSION;
  wrapped.__originalFetch = originalFetch;
  window.fetch = wrapped;
}

// If this call targets our own API and there's a token in sessionStorage,
// merge an Authorization header into the request. Never touches calls that
// already carry an Authorization header (caller wins), never touches the
// auth endpoints themselves (they're anonymous), and never touches
// cross-origin calls (cookies/headers shouldn't leak to third parties).
function withAuthHeader(args) {
  const [input, init] = args;
  if (!isOwnApiCall(input) || isAuthEndpoint(input)) return args;
  const token = getToken();
  if (!token) return args;

  // Detect existing Authorization across the three shapes init.headers can take:
  // a plain object, a Headers instance, or an array of [k, v] pairs.
  const hasAuth = (h) => {
    if (!h) return false;
    if (typeof Headers !== 'undefined' && h instanceof Headers) return h.has('Authorization');
    if (Array.isArray(h)) return h.some(([k]) => String(k).toLowerCase() === 'authorization');
    return Object.keys(h).some((k) => k.toLowerCase() === 'authorization');
  };

  // Request object path — input is a Request with its own headers.
  if (typeof Request !== 'undefined' && input instanceof Request) {
    if (input.headers.has('Authorization')) return args;
    const newHeaders = new Headers(input.headers);
    newHeaders.set('Authorization', `Bearer ${token}`);
    const newReq = new Request(input, { headers: newHeaders });
    return [newReq, init];
  }

  // String/URL path — merge into init.headers.
  const existing = init && init.headers;
  if (hasAuth(existing)) return args;
  const merged = { ...(existing || {}), Authorization: `Bearer ${token}` };
  return [input, { ...(init || {}), headers: merged }];
}

function urlOf(input) {
  try {
    return typeof input === 'string' ? input : (input && input.url) || '';
  } catch {
    return '';
  }
}

function isOwnApiCall(input) {
  const url = urlOf(input);
  if (!url) return false;
  if (url.startsWith('/api/')) return true;
  const apiBase = process.env.REACT_APP_API_URL;
  if (apiBase && url.startsWith(apiBase)) return true;
  return false;
}

// /api/Auth/* — login, OTP, password reset etc. 401 here means "bad
// credentials", not "session expired", so the interceptor must ignore it.
function isAuthEndpoint(input) {
  const url = urlOf(input);
  if (!url) return false;
  // Match both relative (/api/Auth/...) and absolute (https://host/api/Auth/...)
  // forms, case-insensitive on the Auth segment.
  return /\/api\/Auth\//i.test(url);
}
